import history from '../../history';
import {
  getStepByInitRoute,
  parseInitUrlFilterState,
  handleListUpdate,
} from '../../utils/siteSettingsUtils';
import {
  filterSteps,
  filterStepsTypes,
  destinationTraitNames,
  selectablesNames,
  destinationTraits,
} from '../../appSettings';
import { ACTION_TYPES } from '../constants/actionTypes/siteSettings';
import { logFilterEventIfNeeded, logToDataLayer } from '../../utils/tracking';

const setFilters = filters => (
  {
    type: ACTION_TYPES.SET_FILTERS,
    payload: {
      filters,
    },
  }
)

export const setActiveFilterStep = step => ({
  type: ACTION_TYPES.SET_ACTIVE_FILTER_STEP,
  payload: {
    step,
  },
});

export const appInit = () => {
  return async (dispatch, getState) => {
    const { classifications, hotelProducts, conceptHotels, departureAirports } = getState().siteSettings;

    const search = history.location.search.substring(1);
    if (search === '') {
      return
    }
    const parsedQueryParams = JSON.parse(`{"${decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g,'":"')}"}`);
    const initFiltersFromUrl = parseInitUrlFilterState(parsedQueryParams, classifications, hotelProducts, conceptHotels, departureAirports)
    dispatch(setFilters(initFiltersFromUrl));
  }
}

export const handleRouteLoad = (pathname) => {
  return (dispatch, getState) => {
    // Set the current step in the progressbar
    const filterStep = pathname ? getStepByInitRoute(pathname.replace('/', '')) : false
    if (filterStep) {
      dispatch(setActiveFilterStep(filterSteps[filterStep.index]));
      if (filterSteps[filterStep.index].id === filterStepsTypes.DESTINATION_TRAITS_DETAILS) {
        const { siteSettings } = getState();
        logToDataLayer({ event: 'vacationfinder', eventAction: `${destinationTraitNames[siteSettings.filters.selectedPreference]} - View` });
      } else {
        logToDataLayer({ event: 'vacationfinder', eventAction: `${filterSteps[filterStep.index].name} - View` });
      }
      
    }
  }
}

export const handlePrimaryClassificationSelect = (classification) => {
  return (dispatch, getState) => {
    const { siteSettings } = getState()
    const { selectedPrimaryClassifications } = siteSettings.filters;
    const updatedClassifications = handleListUpdate(classification, selectedPrimaryClassifications);
    logFilterEventIfNeeded(siteSettings, selectedPrimaryClassifications, classification, selectablesNames.PRIMARY_CLASSIFICATIONS);
    
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_PRIMARY_CLASSIFICATION,
      payload: {
        selectedClassifications: updatedClassifications,
      },
    });

    /*
      If the user unchecks a primary classification
      We have to uncheck all the child classifications (secondaries)
      of that classification
    */
    if (!updatedClassifications.includes(classification)) {
      const { classifications } = siteSettings;
      const { selectedSecondaryClassifications } = siteSettings.filters;
      const removedClassificationObject = classifications[classification];
      const { children } = removedClassificationObject;
      const childList = Object.keys(children);
      const secondariesToKeep = selectedSecondaryClassifications.filter(secondary => !childList.includes(secondary));

      dispatch({
        type: ACTION_TYPES.SET_SELECTED_SECONDARY_CLASSIFICATION,
        payload: {
          selectedClassifications: secondariesToKeep,
        },
      })
    }
  }
};

export const handleSecondaryClassificationSelect = classification => {
  return (dispatch, getState) => {
    const { siteSettings } = getState()
    const { selectedSecondaryClassifications } = siteSettings.filters;
    const updatedClassifications = handleListUpdate(classification, selectedSecondaryClassifications)
    logFilterEventIfNeeded(siteSettings, selectedSecondaryClassifications, classification, selectablesNames.SECONDARY_CLASSIFICATIONS);
    
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_SECONDARY_CLASSIFICATION,
      payload: {
        selectedClassifications: updatedClassifications,
      },
    })
  }
}

export const handleHotelProductSelect = (hotelProduct) => {
  return (dispatch, getState) => {
    const { siteSettings } = getState()
    const { selectedHotelProducts } = siteSettings.filters;
    const updatedHotelProducts = handleListUpdate(hotelProduct, selectedHotelProducts)
    logFilterEventIfNeeded(siteSettings, selectedHotelProducts, hotelProduct, selectablesNames.HOTEL_PRODUCTS);

    dispatch({
      type: ACTION_TYPES.SET_SELECTED_HOTEL_PRODUCT,
      payload: {
        selectedHotelProducts: updatedHotelProducts,
      },
    })
  }
}

export const handleConceptHotelSelect = (conceptHotel) => {
  return (dispatch, getState) => {
    const { siteSettings } = getState()
    const { selectedConceptHotels } = siteSettings.filters;
    const updatedConceptHotels = handleListUpdate(conceptHotel, selectedConceptHotels)
    logFilterEventIfNeeded(siteSettings, selectedConceptHotels, conceptHotel, selectablesNames.CONCEPT_HOTELS);

    dispatch({
      type: ACTION_TYPES.SET_SELECTED_CONCEPT_HOTEL,
      payload: {
        selectedConceptHotels: updatedConceptHotels,
      },
    })
  }
}

export const handleRatingSelect = (rating) => {
  return (dispatch, getState) => {
    const { siteSettings } = getState()
    const { selectedRatings } = siteSettings.filters;
    const updatedRatings = handleListUpdate(rating, selectedRatings);
    logFilterEventIfNeeded(siteSettings, selectedRatings, rating, selectablesNames.RATING);

    dispatch({
      type: ACTION_TYPES.SET_SELECTED_RATING,
      payload: {
        selectedRatings: updatedRatings,
      },
    })
  }
}

export const handlePreferenceSelect = preference => (
  dispatch => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_PREFERENCE,
      payload: {
        preference,
      },
    })
  }
)

export const handleLocationSelect = location => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, location, destinationTraitNames[destinationTraits.LOCATION]);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_LOCATION,
      payload: {
        location,
      },
    })
  }
)

export const handleFlightTimeSelect = flightTime => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, flightTime, destinationTraitNames[destinationTraits.FLIGHT_TIME]);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_FLIGHT_TIME,
      payload: {
        flightTime,
      },
    })
  }
)

export const handleTemperaturePeriodSelect = temperaturePeriod => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, temperaturePeriod, destinationTraitNames[destinationTraits.TEMPERATURE_PERIOD]);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_TEMPERATURE_PERIOD,
      payload: {
        temperaturePeriod,
      },
    })
  }
)

export const handleTemperatureSelect = temperature => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, temperature, destinationTraitNames[destinationTraits.TEMPERATURE]);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_TEMPERATURE,
      payload: {
        temperature,
      },
    })
  }
)

export const handleNumberOfAdultsSelect = numberOfAdults => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, numberOfAdults, selectablesNames.NUMBER_OF_ADULTS);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_NUMBER_OF_ADULTS,
      payload: {
        numberOfAdults,
      },
    })
  }
)
export const handleNumberOfChildrenSelect = numberOfChildren => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, numberOfChildren, selectablesNames.NUMBER_OF_CHILDREN);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_NUMBER_OF_CHILDREN,
      payload: {
        numberOfChildren,
      },
    })
  }
)
export const handleDepartureSelect = (selectedDeparture, selectedDepartureCaIdDict) => (
  (dispatch, getState) => {
    const { siteSettings } = getState();
    logFilterEventIfNeeded(siteSettings, null, selectedDeparture, selectablesNames.DEPARTURE);
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_DEPARTURE,
      payload: {
        selectedDeparture,
        selectedDepartureCaIdDict,
      },
    })
  }
);

export const handleDepartureMonthsSelect = selectedDepartureMonths => (
  dispatch => {
    dispatch({
      type: ACTION_TYPES.SET_SELECTED_DEPARTURE_MONTHS,
      payload: {
        selectedDepartureMonths,
      },
    });
  }
);
